import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import {
  type FacilitatorInvitePreviewData,
  FacilitatorInvitePreviewState,
} from '@elevate/konnect-api-types';

import { apiClient } from '../../api-client';

/**
 * Fallback page for Facilitator invite links (`/i/:code`).
 *
 * With the app installed, universal/app links open konnect-mobile directly and
 * this page never renders. Otherwise it shows who invited you and when (public
 * preview: first name and time only), tries the app's custom scheme, and offers
 * the store buttons. After installing and signing up, the app returns to the
 * invite.
 */

const APP_STORE_URL = import.meta.env.VITE_APP_STORE_URL;
const PLAY_STORE_URL = import.meta.env.VITE_PLAY_STORE_URL;

const CLOSED_COPY: Record<
  Exclude<FacilitatorInvitePreviewState, FacilitatorInvitePreviewState.Open>,
  { title: string; body: string }
> = {
  [FacilitatorInvitePreviewState.Accepted]: {
    title: 'You already accepted this invite',
    body: 'Open konnect better to see your session.',
  },
  [FacilitatorInvitePreviewState.Expired]: {
    title: 'This invite has expired',
    body: 'Ask the person who invited you to send a new one.',
  },
  [FacilitatorInvitePreviewState.Closed]: {
    title: 'This invite is no longer active',
    body: 'It was declined or cancelled. Ask the person who invited you to send a new one.',
  },
};

function ClosedInvite({
  copy,
  openAppUrl,
}: {
  copy: { title: string; body: string };
  openAppUrl?: string;
}) {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="text-center max-w-md mx-auto">
        <h1 className="text-3xl font-bold text-primary dark:text-theme-primary mb-4">
          {copy.title}
        </h1>
        <p className="text-lg text-muted mb-8">{copy.body}</p>
        {openAppUrl && (
          <a href={openAppUrl} className="btn-primary inline-block">
            Open in konnect better
          </a>
        )}
      </div>
    </div>
  );
}

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; preview: FacilitatorInvitePreviewData }
  | { kind: 'invalid' };

function formatWhen(preview: FacilitatorInvitePreviewData): string {
  const start = new Date(preview.scheduledStart);
  const date = new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(start);
  const time = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(start);
  return `${date} at ${time} for ${preview.durationMinutes} minutes`;
}

export default function InvitePage() {
  const { code = '' } = useParams<{ code: string }>();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const customSchemeUrl = `konnectbetter://i/${encodeURIComponent(code)}`;

  useEffect(() => {
    let active = true;
    apiClient.facilitator
      .previewFacilitatorInvite(code.toUpperCase())
      .then(
        (response) =>
          active && setState({ kind: 'ready', preview: response.data.data }),
      )
      .catch(() => active && setState({ kind: 'invalid' }));
    return () => {
      active = false;
    };
  }, [code]);

  if (state.kind === 'loading') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="w-12 h-12 border-4 border-secondary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (state.kind === 'invalid')
    return (
      <ClosedInvite copy={CLOSED_COPY[FacilitatorInvitePreviewState.Expired]} />
    );

  const { preview } = state;
  if (preview.state !== FacilitatorInvitePreviewState.Open) {
    return (
      <ClosedInvite
        copy={CLOSED_COPY[preview.state]}
        openAppUrl={
          preview.state === FacilitatorInvitePreviewState.Accepted
            ? customSchemeUrl
            : undefined
        }
      />
    );
  }
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-md mx-auto"
      >
        <h1 className="text-3xl font-bold text-primary dark:text-theme-primary mb-4">
          {preview.hostFirstName} invited you to a facilitated session
        </h1>
        <p className="text-lg text-theme-secondary mb-2">
          A space to talk things through, together.
        </p>
        <p className="text-theme-secondary mb-8">{formatWhen(preview)}</p>

        <a href={customSchemeUrl} className="btn-primary mb-6 inline-block">
          Open in konnect better
        </a>

        {(APP_STORE_URL || PLAY_STORE_URL) && (
          <>
            <p className="text-sm text-muted mb-4">
              Don&apos;t have the app yet? Download it, sign up, and your invite
              will be waiting.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {APP_STORE_URL && (
                <a
                  href={APP_STORE_URL}
                  className="btn-secondary"
                  rel="noopener noreferrer"
                >
                  Download on the App Store
                </a>
              )}
              {PLAY_STORE_URL && (
                <a
                  href={PLAY_STORE_URL}
                  className="btn-secondary"
                  rel="noopener noreferrer"
                >
                  Get it on Google Play
                </a>
              )}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
